import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { dictionaries, DEFAULT_LOCALE, SUPPORTED_LOCALES } from './dictionaries';

const STORAGE_KEY = 'driveros:locale';
const I18nContext = createContext(null);

function nestedValue(source, key) {
  return key.split('.').reduce((value, part) => (value && Object.prototype.hasOwnProperty.call(value, part) ? value[part] : undefined), source);
}

function interpolate(value, variables) {
  // Some translations intentionally carry structured data (for example a
  // driver availability label and its explanatory hint). Keep that shape
  // intact instead of coercing arrays into a comma-separated string.
  if (Array.isArray(value)) return value.map((entry) => interpolate(entry, variables));
  if (typeof value !== 'string') return value;
  return value.replace(/{{\s*([\w.-]+)\s*}}/g, (_, key) => variables?.[key] ?? `{{${key}}}`);
}

function initialLocale() {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (SUPPORTED_LOCALES.includes(saved)) return saved;
  } catch (_) { /* private browser mode */ }
  const browser = navigator.language?.slice(0, 2).toLowerCase();
  return SUPPORTED_LOCALES.includes(browser) ? browser : DEFAULT_LOCALE;
}

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(initialLocale);
  const setLocale = (nextLocale) => {
    if (!SUPPORTED_LOCALES.includes(nextLocale)) return;
    setLocaleState(nextLocale);
  };
  useEffect(() => {
    document.documentElement.lang = locale;
    try { window.localStorage.setItem(STORAGE_KEY, locale); } catch (_) { /* storage is optional */ }
  }, [locale]);
  const value = useMemo(() => ({
    locale,
    locales: SUPPORTED_LOCALES,
    setLocale,
    t(key, variables) {
      const resolved = nestedValue(dictionaries[locale], key) ?? nestedValue(dictionaries[DEFAULT_LOCALE], key);
      return interpolate(resolved ?? key, variables);
    },
  }), [locale]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n must be used inside I18nProvider');
  return context;
}
